import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, RefreshCwIcon, CreditCardIcon, UserIcon, BuildingIcon, FileTextIcon } from 'lucide-react';
import { Sale } from '../../../shared/types/Transaction';
import { RefundModal } from '../../refunds/components/RefundModal';
import { TransactionFinancialBreakdown } from '../../../shared/ui/TransactionFinancialBreakdown';
import { SCENARIOS, buildScenario } from '../../payments/utils/transactionScenarios';
import { getMockSaleById, getSaleFixture } from '../../../shared/mocks/mockSales';
import { computeTransactionViewModel } from '../../payments/utils/transactionViewModel';
import { formatMoney } from '../../../shared/lib/money';
export function SaleDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  // Get sale fixture and initialize scenario from it
  const saleFixture = getSaleFixture(id || 'payment-with-reserve');
  const [selectedScenarioId, setSelectedScenarioId] = useState(saleFixture?.defaultScenarioId || 'payment-with-reserve');
  // Get base sale data from shared fixtures
  const baseSale = getMockSaleById(id || 'payment-with-reserve');
  // Compute current scenario data (updates when scenario changes)
  const currentScenario = useMemo(() => {
    return buildScenario(selectedScenarioId);
  }, [selectedScenarioId]);
  const currentViewModel = useMemo(() => {
    return computeTransactionViewModel(currentScenario.transaction, currentScenario.ledgerRows, 'included');
  }, [currentScenario]);
  // Build sale object with current scenario data
  const sale: Sale = useMemo(() => {
    if (!baseSale) {
      // Fallback if sale not found
      return {
        id: id || 'unknown',
        transactionId: currentScenario.transaction.transactionId,
        amount: currentViewModel.headerAmount,
        currency: currentScenario.transaction.currency,
        status: 'completed',
        source: 'payment_link',
        date: currentScenario.transaction.date,
        customerName: 'Unknown Customer',
        customerEmail: 'unknown@example.com',
        paymentMethod: {
          type: 'card'
        },
        merchantName: 'Unknown Merchant',
        merchantId: 'MERCH-000',
        description: currentScenario.description,
        fee: currentViewModel.summary.feesPostedDisplay,
        netAmount: currentViewModel.summary.netPosted,
        lifecycle: []
      };
    }
    // Override transaction data with current scenario
    return {
      ...baseSale,
      transactionId: currentScenario.transaction.transactionId,
      amount: currentViewModel.headerAmount,
      currency: currentScenario.transaction.currency,
      date: currentScenario.transaction.date,
      fee: currentViewModel.summary.feesPostedDisplay,
      netAmount: currentViewModel.summary.netPosted
    };
  }, [baseSale, currentScenario, currentViewModel, id]);
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      refund_initiated: 'bg-emerald-100 text-emerald-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-12572" data-mp="2ly3k9ermyj56">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const handleRefund = (amount: number, reason: string, notes: string) => {
    console.log('Refund initiated:', {
      transactionId: sale.transactionId,
      amount,
      reason,
      notes
    });
    // In a real app, this would call an API to process the refund
    alert(`Refund of ${formatMoney(amount, sale.currency)} initiated successfully!`);
  };
  return <div className="space-y-6" data-id="element-12573" data-mp="f76wbrpenx9o">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-12574" data-mp="f4krhztwfvv5">
        <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-12575" data-mp="a8m003d9xxqi">
          <ArrowLeftIcon size={20} data-id="element-12576" data-mp="3il19uxrwsdub" />
          {t('pages.SaleDetail.back_to_sales')}
        </button>
        <div className="flex items-center gap-3" data-id="element-12577" data-mp="3dmgd58bv8cam">
          {/* Dev-only scenario selector */}
          <select value={selectedScenarioId} onChange={e => setSelectedScenarioId(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50" data-id="element-12578" data-mp="3sf0d603py3nu">
            {SCENARIOS.map(scenario => <option key={scenario.scenarioId} value={scenario.scenarioId} data-id="element-12579" data-mp="2uvp64dfcoxze">
                {scenario.description}
              </option>)}
          </select>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-12580" data-mp="1f791zayimyqg">
            <DownloadIcon size={18} data-id="element-12581" data-mp="11gxane9r940d" />
            {t('pages.SaleDetail.download_receipt')}
          </button>
          {sale.status === 'completed' && <button onClick={() => setRefundModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700" data-id="element-12582" data-mp="1t5qjus1qph0g">
              <RefreshCwIcon size={18} data-id="element-12583" data-mp="1k63niknvb1e9" />
              {t('pages.SaleDetail.refund')}
            </button>}
        </div>
      </div>
      {/* Transaction Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-12584" data-mp="8qexfkfbhm98">
        <div className="flex items-start justify-between mb-6" data-id="element-12585" data-mp="8p3vzr9cgqla">
          <div data-id="element-12586" data-mp="dqjd588eicye">
            <div className="text-4xl font-bold text-gray-900" data-id="element-12587" data-mp="dr2ykyx9fjh6">
              {formatMoney(sale.amount, sale.currency)}
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mt-2" data-id="element-12588" data-mp="2afksmaxquo9h">
              {sale.transactionId}
            </h1>
          </div>
          <div data-id="element-12589" data-mp="25o2q3ig2ofme">{getStatusBadge(sale.status)}</div>
        </div>

        {/* Transaction Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12590" data-mp="21m7rwmtfuicu">
          {/* Customer Information */}
          <div className="space-y-4" data-id="element-12591" data-mp="1wp7hy0tgwvgg">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12592" data-mp="1wm392q525bjg">
              <UserIcon size={20} className="text-blue-600" data-id="element-12593" data-mp="1rnrzmgiod31r" />
              {t('pages.OrderDetail.customer_information')}
            </h3>
            <div className="space-y-3" data-id="element-12594" data-mp="1ioog5bflilmq">
              <div data-id="element-12595" data-mp="1ipq0k6ii4kh4">
                <div className="text-sm text-gray-500" data-id="element-12596" data-mp="1iq7yr8oq58ro">
                  {t('pages.SaleDetail.name')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12597" data-mp="3d2fwetsfk3r5">
                  {sale.customerName}
                </div>
              </div>
              <div data-id="element-12598" data-mp="ytvhau9y9jew">
                <div className="text-sm text-gray-500" data-id="element-12599" data-mp="yueq9lhmgxgs">
                  {t('pages.OrderDetail.email')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12600" data-mp="18kgdkb25ibun">
                  {sale.customerEmail}
                </div>
              </div>
              {sale.customerPhone && <div data-id="element-12601" data-mp="1cnw9t6ab6a6t">
                  <div className="text-sm text-gray-500" data-id="element-12602" data-mp="fw2f5u75hdik">
                    {t('pages.SaleDetail.phone')}
                  </div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-12603" data-mp="kvos02j2grqu">
                    {sale.customerPhone}
                  </div>
                </div>}
            </div>
          </div>
          {/* Payment Information */}
          <div className="space-y-4" data-id="element-12604" data-mp="tt7hpk7nnu92">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12605" data-mp="20cfhpkpg4kyd">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-12606" data-mp="25a7jr7jt1nga" />
              {t('pages.OrderDetail.payment_information')}
            </h3>
            <div className="space-y-3" data-id="element-12607" data-mp="1gyopqd4t5gpi">
              <div data-id="element-12608" data-mp="1bz2h6gzxcon1">
                <div className="text-sm text-gray-500" data-id="element-12609" data-mp="1bzlutlijkqv9">
                  {t('pages.OrderDetail.payment_method')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12610" data-mp="1ps1s05orhuo9">
                  {sale.paymentMethod.brand || sale.paymentMethod.type}{' '}
                  {sale.paymentMethod.last4 && `•••• ${sale.paymentMethod.last4}`}
                  {sale.paymentMethod.details && sale.paymentMethod.details}
                </div>
              </div>
              <div data-id="element-12611" data-mp="28pmifh76d1az">
                <div className="text-sm text-gray-500" data-id="element-12612" data-mp="28o1ptx4f7sfr">
                  {t('pages.SaleDetail.transaction_date')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12613" data-mp="24lwwz0lsqzu6">
                  {new Date(sale.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              <div data-id="element-12614" data-mp="6bft9kpdie7">
                <div className="text-sm text-gray-500" data-id="element-12615" data-mp="85s5zhws26o">
                  {t('pages.SaleDetail.source')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12616" data-mp="3mlmjjzcolz2i">
                  {sale.source.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </div>
              </div>
            </div>
          </div>
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-12617" data-mp="3hnkzbb3irtpg">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12618" data-mp="33ms22ilu2kin">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-12619" data-mp="38llhigkokllg" />
              {t('pages.SaleDetail.merchant_information')}
            </h3>
            <div className="space-y-3" data-id="element-12620" data-mp="11w6vvd8reqh3">
              <div data-id="element-12621" data-mp="11t2m80ciz2j1">
                <div className="text-sm text-gray-500" data-id="element-12622" data-mp="16tylu1pug2in">
                  {t('pages.SaleDetail.merchant_name')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12623" data-mp="3ptz6zc1vmczs">
                  {sale.merchantName}
                </div>
              </div>
              <div data-id="element-12624" data-mp="3unldsnlczmno">
                <div className="text-sm text-gray-500" data-id="element-12625" data-mp="3gulyomphnugn">
                  {t('pages.SaleDetail.merchant_id')}
                </div>
                <div className="text-sm font-medium text-gray-900" data-id="element-12626" data-mp="3kwyzcwpdp26k">
                  {sale.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Additional Details */}
          {sale.invoiceId && <div className="space-y-4" data-id="element-12627" data-mp="hcuon2xnu3u9">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-12628" data-mp="3g8w87gkd778">
                <FileTextIcon size={20} className="text-blue-600" data-id="element-12629" data-mp="8dr55xukzmf8" />
                {t('pages.SaleDetail.related_documents')}
              </h3>
              <div className="space-y-3" data-id="element-12630" data-mp="2o4ddelznotxu">
                <div data-id="element-12631" data-mp="2t4hry6h07elf">
                  <div className="text-sm text-gray-500" data-id="element-12632" data-mp="2t5sww9m6qse4">
                    {t('pages.SaleDetail.invoice')}
                  </div>
                  <button onClick={() => navigate(`/invoice-detail/${sale.invoiceId}`)} className="text-sm font-medium text-blue-600 hover:underline" data-id="element-12633" data-mp="2klxthqk8qi3s">
                    {sale.invoiceId}
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* New Financial Breakdown */}
      <TransactionFinancialBreakdown transactionId={sale.transactionId} scenarioId={selectedScenarioId} currency={sale.currency} data-id="element-12634" data-mp="1wyoi3wwb57gr" />

      {/* Transaction Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-12635" data-mp="26vu9l68u30hi">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-12636" data-mp="26uj9phiiecvo">
          {t('pages.SaleDetail.transaction_lifecycle')}
        </h2>
        <div className="space-y-6" data-id="element-12637" data-mp="1e8glzyr0zha2">
          {currentScenario.ledgerRows.filter(row => row.status === 'Posted').map((row, index, filteredRows) => <div key={row.id} className="relative" data-id="element-12638" data-mp="3j8rfg8fatl50" data-mpi={index}>
                {index !== filteredRows.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-12639" data-mp="3nzhj9h82tfxl"></div>}
                <div className="flex items-start gap-4" data-id="element-12640" data-mp="3s1l06ldilcq9">
                  <div className="flex-shrink-0" data-id="element-12641" data-mp="3s35uqaztwvo4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-12642" data-mp="2vcdm1yf865wd">
                      <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-12643" data-mp="30aybr3wup76k"></div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-12644" data-mp="34duyn6tfbpgb">
                    <div className="flex items-center justify-between" data-id="element-12645" data-mp="39b3gt7eknthk">
                      <div className="flex items-center gap-3" data-id="element-12646" data-mp="jlrs8ux919kt">
                        <p className="text-sm font-medium text-gray-900" data-id="element-12647" data-mp="jl9pdfg2zx3x">
                          {row.posting}
                        </p>
                        {getStatusBadge(row.status.toLowerCase())}
                      </div>
                      <p className="text-sm text-gray-500" data-id="element-12648" data-mp="tpq23zy9ky3i">{row.effectiveAt}</p>
                    </div>
                  </div>
                </div>
              </div>)}
        </div>
      </div>

      {/* Refund Modal */}
      <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={sale.amount} transactionId={sale.transactionId} currency="MYR" onRefund={handleRefund} data-id="element-12649" data-mp="kw3beo8wyl16" />
    </div>;
}